import axios from "axios";
import { LENDING_CONTRACT_ADDRESS } from "../lib/constants";
import chains from "./chains";
import { helperConstants as constants } from "./helperConstants";
import { GetContractResult, waitForTransaction } from "wagmi/actions";
import { wrappedTokenABI } from "./wrappedTokenABI";
import { Address, WalletClient } from "viem";
import erc721ABI from "./erc721ABI";

const MaxUint256 = BigInt(
  "0xffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff"
);

export const approveERC20 = async (
  tokenContract: GetContractResult<typeof wrappedTokenABI, WalletClient>,
  walletAddress: Address,
  amount: string
) => {
  try {
    const approvedAmount = await tokenContract.read.allowance([
      walletAddress,
      LENDING_CONTRACT_ADDRESS,
    ]);

    if (BigInt(approvedAmount) < BigInt(amount)) {
      const tx = await tokenContract.write.approve(
        [LENDING_CONTRACT_ADDRESS, MaxUint256],
        {}
      );
      await waitForTransaction({ hash: tx });
    }
  } catch (err) {
    throw err;
  }
};

export const approveNFT = async (nftContract, walletaddress, tokenId) => {
  try {
    const isApprovedForAll = await nftContract.read.isApprovedForAll([
      walletaddress,
      LENDING_CONTRACT_ADDRESS,
    ]);

    if (isApprovedForAll) return true;

    const tx = await nftContract.write.setApprovalForAll([
      LENDING_CONTRACT_ADDRESS,
      true,
    ]);
    await waitForTransaction({ hash: tx });
    return true;
  } catch (err) {
    throw err;
  }
};

export const approveNFTLending = async (
  contract: GetContractResult<typeof erc721ABI, WalletClient>,
  walletaddress: string
) => {
  try {
    const isApprovedForAll = await contract.read.isApprovedForAll([
      walletaddress,
      LENDING_CONTRACT_ADDRESS,
    ]);

    if (isApprovedForAll) return true;

    const tx = await contract.write.setApprovalForAll([
      LENDING_CONTRACT_ADDRESS,
      true,
    ]);
    await waitForTransaction({ hash: tx });
    return true;
  } catch (err) {
    throw err;
  }
};

export const approveSelectedCurrency = async (
  tokenContract: GetContractResult<typeof wrappedTokenABI, WalletClient>,
  walletAddress: Address,
  borrowAmount?: string
) => {
  try {
    let currencyAllowance = await tokenContract.read.allowance([
      walletAddress,
      LENDING_CONTRACT_ADDRESS,
    ]);

    if (currencyAllowance < BigInt(borrowAmount)) {
      const tx = await tokenContract.write.approve(
        [LENDING_CONTRACT_ADDRESS, MaxUint256],
        {}
      );
      await waitForTransaction({ hash: tx });
    }
  } catch (err) {
    throw err;
  }
};

export const getTokenBalance = async (tokenContract, walletAddress) => {
  try {
    return await tokenContract.read.balanceOf([walletAddress]);
  } catch (err) {
    return 0;
  }
};

export const checkChain = async (chainHex, ethereum: any) => {
  const chain = chains[chainHex];
  try {
    // if chain added, switch
    await ethereum.request({
      method: "wallet_switchEthereumChain",
      params: [{ chainId: chainHex }],
    });
  } catch (switchError) {
    // if chain is not added, request to add chain
    if (switchError.code === 4902) {
      try {
        await ethereum.request({
          method: "wallet_addEthereumChain",
          params: [
            {
              chainId: chainHex,
              chainName: chain.name,
              rpcUrls: chain.rpcUrls,
              blockExplorerUrl: chain.blockExplorerUrl,
            },
          ],
        });
      } catch (addError) {
        throw "Adding " + chain.name + " failed";
      }
    }
  }
};

export const cancelOrder = async (
  marketplaceContract,
  signature,
  kind,
  chain,
  setText
) => {
  try {
    const order = (
      await axios.get(`${constants.api.url_new}/orders/getOrder`, {
        params: {
          signature,
          kind,
          chain,
        },
      })
    ).data.data;

    const tx = await marketplaceContract.write.cancelOrder([
      {
        issuer: order?.issuer,
        nftAddress: order?.collectionAddress,
        paymentToken: order?.paymentToken,
        price: order?.price,
        tokenId: order?.globalBidAmount > 0 ? "0" : order?.itemId,
        end: order?.end,
        kind: order?.kind,
        tokenKind: order?.tokenKind,
        globalBidAmount: order?.globalBidAmount,
        privileges: {
          privilegedCollection: order.privilegedCollection,
          privilegedTokenId: order.privilegedTokenId,
        },
      },
      signature,
    ]);

    setText("Waiting blockchain...");
    await waitForTransaction({ hash: tx });
    return tx;
  } catch (err) {
    console.log(err);
    throw err;
  }
};
